import React from 'react';
import { Image, Flex, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../../../../Shared/GlobalContext/ThemeContext';
import AudioMessage from '../AudioMessage/AudioMessage';
import VideoPlayer from '../VideoPlayer/VideoPlayer';
import VideoFailedPlaceholder from '../VideoPlayer/VideoFailedPlaceholder';

const { Text } = Typography;

const AUDIO_EXTENSIONS = /\.(mp3|wav|ogg|m4a|flac|aac|weba)$/i;
const RECORDED_VOICE_WEBM = /(?:^|_)voice_\d+\.webm$/i;

const getUrlPath = (url) => {
    if (typeof url !== 'string') return '';
    return url.split('?')[0].split('#')[0];
};

export const resolveMediaDisplayType = (media = {}) => {
    const mediaType = media.media_type;
    const data = media.data_json || {};
    const mimeType = (media.mime_type || data.mime_type || data.content_type || '').toLowerCase();
    const urlPath = getUrlPath(media.url);
    const fileNames = [
        media.filename,
        media.file_name,
        data.filename,
        data.file_name,
        data.original_filename,
        urlPath.split('/').pop()
    ].filter(Boolean);
    const hasAudioFileName = fileNames.some(fileName => (
        AUDIO_EXTENSIONS.test(fileName) || RECORDED_VOICE_WEBM.test(fileName)
    ));

    if (mediaType === 'audio' || mimeType.startsWith('audio/')) {
        return 'audio';
    }

    if (hasAudioFileName) {
        return 'audio';
    }

    if (
        mediaType === 'video' &&
        data.has_audio === true &&
        !data.width &&
        !data.height &&
        typeof data.duration === 'number'
    ) {
        return 'audio';
    }

    if (mediaType === 'video' || mimeType.startsWith('video/')) {
        return 'video';
    }

    return mediaType;
};

const MessageMediaGallery = ({
    mediaList,
    isCustomerMessage,
    isDarkMode,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleTouchCancel
}) => {
    const { t } = useTranslation();
    const { isDarkMode: themeDarkMode } = useTheme();

    // Allow overriding darkmode but default to context
    const darkMode = isDarkMode !== undefined ? isDarkMode : themeDarkMode;

    if (!Array.isArray(mediaList) || mediaList.length === 0) return null;

    return (
        <>
            {mediaList.map((media, index) => {
                const displayType = resolveMediaDisplayType(media);

                return (
                    <div key={index} style={{ width: 'auto', maxWidth: '100%' }}>
                        {displayType === 'audio' && (
                            <AudioMessage
                                url={media.url}
                                duration={media.data_json?.duration}
                                isCustomerMessage={isCustomerMessage}
                                sender={media.sender_type}
                            />
                        )}
                        {displayType === 'video' && (
                            media.processing_stage === 'failed' ? (
                                <VideoFailedPlaceholder
                                    error={media.processing_error}
                                    isCustomerMessage={isCustomerMessage}
                                    isDarkMode={darkMode}
                                />
                            ) : (
                                <VideoPlayer
                                    url={media.url}
                                    duration={media.data_json?.duration}
                                    isCustomerMessage={isCustomerMessage}
                                />
                            )
                        )}
                        {displayType === 'image' && (
                            <div
                                style={{
                                    background: darkMode ? '#f0f0f0' : '#ffffff',
                                    borderRadius: 4,
                                    padding: 4,
                                    overflow: 'hidden',
                                    display: 'inline-block',
                                    maxWidth: '100%'
                                }}
                                onTouchStart={handleTouchStart}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                                onTouchCancel={handleTouchCancel}
                            >
                                <Image
                                    src={media.url}
                                    alt={t('message_attachment')}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '350px',
                                        width: 'auto',
                                        height: 'auto',
                                        borderRadius: 4,
                                        display: 'block',
                                        objectFit: 'contain'
                                    }}
                                />
                            </div>
                        )}
                        {displayType === 'document' && (
                            <div style={{
                                border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                                borderRadius: 4,
                                padding: 8,
                                background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
                            }}>
                                <Flex align="center" gap={8}>
                                    <div style={{ fontSize: '24px' }}>📄</div>
                                    <Flex vertical>
                                        <Text style={{
                                            fontSize: '13px',
                                            color: darkMode ? '#fff' : 'inherit',
                                            fontWeight: 500
                                        }}>
                                            {media.filename || t('document')}
                                        </Text>
                                        <Text
                                            style={{
                                                color: darkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.45)',
                                                fontSize: '11px'
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                window.open(media.url, '_blank');
                                            }}
                                            underline
                                        >
                                            {t('view_document')}
                                        </Text>
                                    </Flex>
                                </Flex>
                            </div>
                        )}
                    </div>
                );
            })}
        </>
    );
};

export default MessageMediaGallery;
